<script setup>
import { stripBlockTags } from '@/composables/helpers/html';

const props = defineProps({
    headline: { type: String, required: false },
    copy: { type: String, required: false },
    // Retained for Builder/CMS back-compat; not shown in the 2026 design.
    subText: { type: String, required: false },
    button: { type: Object, required: false },
    secondButton: { type: Object, required: false },
    image: { type: Object, required: false },
    countdownDate: { type: String, required: false },
    // Retained for Builder/CMS back-compat; not used in the 2026 design.
    straightBackground: { type: Boolean },
    attributes: { type: Object, required: false },
});

// Decorative orange glow shards — the two "Orange Gradient" circles from the
// Figma (node 4492:65995): a blurred #FF4800 circle each. Rasterised from the
// Figma SVGs at their FULL extent (1163² and 605²) so the soft edge fades to
// transparent on every side. The previous assets were frame-CROPS (848×639 /
// 447×216) whose amputated edges showed as a hard line near the top and left
// the upper-right glow off-screen entirely (UNB-558). Static public paths —
// never imported (keeps them out of the Worker bundle).
const sanitizedHeadline = computed(() => stripBlockTags(props.headline));

const shardLeft = '/img/2026/countdown/orange-glow-large.png';
const shardTopRight = '/img/2026/countdown/orange-glow-small.png';

const units = [
    { key: 'days', label: 'Days' },
    { key: 'hours', label: 'Hrs' },
    { key: 'minutes', label: 'Min' },
    { key: 'seconds', label: 'Sec' },
];

const countdownValues = ref({ days: '00', hours: '00', minutes: '00', seconds: '00' });
const clockHitZero = ref(false);

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

let timer = null;

function tick() {
    if (!props.countdownDate) return;
    let delta = new Date(props.countdownDate).getTime() - Date.now();
    if (Number.isNaN(delta)) return;
    if (delta <= 0) {
        clockHitZero.value = true;
        if (timer) clearInterval(timer);
        return;
    }
    const days = Math.floor(delta / 86400000); delta -= days * 86400000;
    const hours = Math.floor(delta / 3600000); delta -= hours * 3600000;
    const minutes = Math.floor(delta / 60000); delta -= minutes * 60000;
    const seconds = Math.floor(delta / 1000);
    countdownValues.value = { days: pad(days), hours: pad(hours), minutes: pad(minutes), seconds: pad(seconds) };
}

onMounted(() => {
    tick();
    timer = setInterval(tick, 1000);
});

onBeforeUnmount(() => {
    if (timer) clearInterval(timer);
});
</script>

<template>
    <section class="block-image-text-countdown" tabindex="-1">
        <div class="card relative overflow-hidden text-white">
            <!-- Decorative orange glow shards (PNG; positioned per Figma) -->
            <img :src="shardLeft" loading="lazy" decoding="async" alt="" aria-hidden="true" class="shard shard-left" />
            <img :src="shardTopRight" loading="lazy" decoding="async" alt="" aria-hidden="true" class="shard shard-top-right" />

            <!-- Image (speaker) — shard-masked, bleeds the left side; sits above the glow shards -->
            <div v-if="image" class="image-col">
                <MessImage :image="image" />
                <!-- magenta→purple gradient baked into the Figma mask -->
                <div class="image-overlay" aria-hidden="true" />
            </div>

            <!-- Content — right-weighted column over the gradient -->
            <div class="content relative z-3 flex flex-col gap-[40px] lg:gap-[49px] px-(--box-padding-lr-m) py-[48px] lg:px-[40px] lg:py-[90px]">
                <div class="flex flex-col gap-[18px]">
                    <!-- stripBlockTags: Builder wraps richtext in a <p>, which legacy
                         typography then gives its own line-height (87px), overriding the
                         66px set here on the h2 (UNB-633). -->
                    <MessHtml v-if="headline" tag="h2"
                        class="font-hubspot-serif font-normal text-[40px] lg:text-[60px] leading-[1.1]"
                        :html="sanitizedHeadline" />
                    <MessHtml v-if="copy"
                        class="font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.02em]"
                        :html="copy" />

                    <ClientOnly>
                        <div v-if="!clockHitZero" class="countdown flex items-center gap-[15px]">
                            <template v-for="(unit, i) in units" :key="unit.key">
                                <span v-if="i > 0" class="countdown-divider" aria-hidden="true" />
                                <div class="flex flex-col items-start gap-[6px] whitespace-nowrap">
                                    <span class="font-hubspot-serif text-[45px] leading-[1.2]">{{ countdownValues[unit.key] }}</span>
                                    <span class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.07em] uppercase">{{ unit.label }}</span>
                                </div>
                            </template>
                        </div>
                    </ClientOnly>
                </div>

                <div v-if="(button && button.label && button.url) || (secondButton && secondButton.label && secondButton.url)" class="flex flex-wrap gap-4">
                    <FragmentsPillButton v-if="button && button.label && button.url"
                        variant="white" :button="button" />
                    <FragmentsPillButton v-if="secondButton && secondButton.label && secondButton.url"
                        variant="white" :button="secondButton" />
                </div>
            </div>
        </div>
    </section>
</template>

<style>
/* Full-bleed gradient band — no contained max-width, no border-radius. */
.block-image-text-countdown .card {
    width: 100%;
    /* linear-gradient(125.89deg, #bc2859 20.19%, --purple 64.97%, #520744 98.32%) */
    background-image: linear-gradient(125.89deg, #bc2859 20.19%, var(--purple) 64.97%, #520744 98.32%);
}

/* Content column: full width on mobile, right ~46% on desktop. The image and
   glow occupy the left side underneath via absolute positioning. */
.block-image-text-countdown .content {
    margin-left: auto;
}
@media (min-width: 1024px) {
    .block-image-text-countdown .card {
        min-height: 600px;
    }
    .block-image-text-countdown .content {
        width: 46%;
        justify-content: center;
    }
}

/* Mobile: stack content above the image (like the CTA block). */
@media (max-width: 1023px) {
    .block-image-text-countdown .card {
        display: flex;
        flex-direction: column;
        /* Reverse the gradient on mobile: darker purple in the upper-left,
           fuchsia toward the bottom-right (desktop runs the other way). */
        background-image: linear-gradient(125.89deg, #520744 2%, var(--purple) 35%, #bc2859 80%);
    }
    .block-image-text-countdown .content {
        order: 1;
    }
    .block-image-text-countdown .image-col {
        order: 2;
    }
}

/* [child] — speaker image, masked to the Figma shard. On mobile it stacks
   above the content; on desktop it bleeds the bottom-left, capped at the
   design width so it doesn't balloon on ultrawide screens. */
.block-image-text-countdown .image-col {
    position: relative;
    z-index: 2;
    width: 100%;
    aspect-ratio: 710 / 542;
    clip-path: url(#countdown-shard);
}
.block-image-text-countdown .image-col .mess-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
}
.block-image-text-countdown .image-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(to bottom,
        rgba(253, 46, 203, 0) 0%,
        rgba(253, 46, 203, 0.32) 75%,
        rgba(130, 12, 167, 0.64) 100%);
}
@media (min-width: 1024px) {
    .block-image-text-countdown .image-col {
        position: absolute;
        left: 0;
        bottom: 0;
        width: 50%;
        max-width: 710px;
    }
}
/* Mobile: scale the shard image down so its right point clears the viewport edge
   instead of touching it. Placed after the base width:100% rule above so it wins. */
@media (max-width: 1023px) {
    .block-image-text-countdown .image-col {
        width: calc(100% - 32px);
    }
}

/* [decoration] — orange glow shards. The PNGs are the full blurred glows, so
   they fade to transparent on every edge; positions match the Figma 1440x639
   frame, verified against the design render (UNB-558). */
.block-image-text-countdown .shard {
    position: absolute;
    z-index: 1;
    pointer-events: none;
    max-width: none;
}
/* Big glow behind the image. Bottom-anchored so it tracks the bottom-aligned
   image rather than drifting up when the card is shorter than the Figma frame,
   with the bright core pulled low-left so the upper-left reads fuchsia (Nick,
   UNB-558) rather than washing orange. */
.block-image-text-countdown .shard-left {
    width: 1163px;
    left: -315px;
    bottom: -360px;
}
/* Small glow peeking into the upper-right corner. Top-anchored with the core
   just inside the top edge so the warm tint actually reads there — the old
   position sat the whole glow off the top of the card, which is why it was
   invisible (Nick, UNB-558). */
.block-image-text-countdown .shard-top-right {
    width: 640px;
    right: -190px;
    top: -300px;
}
@media (max-width: 1023px) {
    /* Hide the glow shards on mobile — clipped by the card edge they read as a
       hard rectangular cut rather than a soft glow. */
    .block-image-text-countdown .shard {
        display: none;
    }
}

/* [divider] — pink colon (two dots) between countdown units; matches the
   Figma 5x40 divider (#F85ED3 circles at cy 7.5 / 21.5, r 2.5) */
.block-image-text-countdown .countdown-divider {
    position: relative;
    flex-shrink: 0;
    width: 5px;
    height: 40px;
    /* Center the colon on the digits, not the digit+label column (the row is
       items-center, which otherwise drops it toward the bottom of the digits). */
    top: -7px;
}
.block-image-text-countdown .countdown-divider::before,
.block-image-text-countdown .countdown-divider::after {
    content: '';
    position: absolute;
    left: 0;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #f85ed3;
}
.block-image-text-countdown .countdown-divider::before { top: 5px; }
.block-image-text-countdown .countdown-divider::after { top: 19px; }
</style>
